/**
 * Does performing this action have to capture an exception first?
 *
 * Two independent facts have to agree:
 *
 * - an ``ExceptionActionBinding`` says an exception is POSSIBLE for the action
 *   (static tenant config), and
 * - eligibility says THIS record actually needs one (`requires_override`,
 *   computed per record by the handler's `check_eligibility`).
 *
 * Kept out of the component so the rule is stated once and testable. Binding
 * alone used to decide it, which filed every in-policy tier change as an
 * override too - so `override_program_tier_logic` came to mean "someone
 * changed a tier" rather than "someone stepped outside the rules". WPS-180's
 * ticket separates the two: ALL tier changes reach the audit history (an
 * EntityLog row is written on execute either way), while only "requirements
 * not met" routes through the exception approval process.
 */
export function needsExceptionCapture(
  hasBinding: boolean,
  requiresOverride: boolean | null | undefined,
): boolean {
  if (!hasBinding) return false;
  // Only an explicit `false` skips the capture, and only a handler's own
  // `check_eligibility` can produce one. `null` (the handler has no notion of
  // eligibility) and `undefined` (an older payload) both KEEP the gate.
  //
  // That distinction is the whole safety property. Most handlers do not
  // implement eligibility - `mark_as_paid`, `bypass_90_day_window`,
  // `cancel_expire_terms`, `change_installer` - and if "no opinion" arrived
  // as `false`, every one of them would read as cleared and skip its
  // exception modal, silently removing the capture the exception system
  // exists for.
  return requiresOverride !== false;
}
